import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@utils/cn';
import { useTranslation } from '@hooks/useTranslation';

interface TablePaginationProps {
  /** Zero-based, to match the API's `page` argument. */
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
  className?: string;
}

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/**
 * Page numbers with an ellipsis where the run is too long to show.
 *
 * Always keeps the first page, the last page, and the current page with one
 * neighbour on each side — so the control never reflows as you walk through it.
 */
const buildPageList = (currentPage: number, totalPages: number): (number | 'gap')[] => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index);
  }

  const pages = new Set<number>([0, totalPages - 1, currentPage]);
  if (currentPage - 1 > 0) pages.add(currentPage - 1);
  if (currentPage + 1 < totalPages - 1) pages.add(currentPage + 1);

  const sorted = Array.from(pages).sort((a, b) => a - b);
  const withGaps: (number | 'gap')[] = [];

  sorted.forEach((pageIndex, position) => {
    if (position > 0 && pageIndex - (sorted[position - 1] as number) > 1) {
      withGaps.push('gap');
    }
    withGaps.push(pageIndex);
  });

  return withGaps;
};

/**
 * Pagination for server-paged tables.
 *
 * Zero-based on purpose: the API is zero-based, and converting at the boundary
 * is where off-by-one bugs come from. Only the labels are 1-based.
 */
export const TablePagination: React.FC<TablePaginationProps> = ({
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  className,
}) => {
  const tCommon = useTranslation('common');
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const firstRow = totalCount === 0 ? 0 : page * pageSize + 1;
  const lastRow = Math.min((page + 1) * pageSize, totalCount);

  const goTo = (nextPage: number) =>
    onPageChange(Math.min(Math.max(0, nextPage), totalPages - 1));

  const arrowClasses = cn(
    'inline-flex h-8 w-8 items-center justify-center rounded-md border transition-colors',
    'border-gray-300 text-gray-600 hover:bg-gray-50',
    'disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent',
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
    'dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800',
  );

  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 pt-3 dark:border-gray-700',
        className,
      )}>
      <div className="flex items-center gap-3">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {tCommon.showingRange
            .replace('{from}', String(firstRow))
            .replace('{to}', String(lastRow))
            .replace('{total}', String(totalCount))}
        </p>

        {onPageSizeChange && (
          <label className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
            <span className="sr-only sm:not-sr-only">{tCommon.rows}</span>
            <select
              value={pageSize}
              onChange={(event) => onPageSizeChange(Number(event.target.value))}
              className={cn(
                'rounded-md border border-gray-300 bg-white py-1 pl-2 pr-7 text-sm',
                'focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500',
                'dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200',
              )}>
              {pageSizeOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      <nav className="flex items-center gap-1" aria-label={tCommon.paginationLabel}>
        <button
          type="button"
          onClick={() => goTo(page - 1)}
          disabled={page === 0}
          aria-label={tCommon.previousPage}
          className={arrowClasses}>
          <ChevronLeft size={16} aria-hidden />
        </button>

        {buildPageList(page, totalPages).map((entry, index) =>
          entry === 'gap' ? (
            <span
              key={`gap-${index}`}
              aria-hidden
              className="px-1 text-sm text-gray-400 select-none">
              …
            </span>
          ) : (
            <button
              key={entry}
              type="button"
              onClick={() => goTo(entry)}
              aria-current={entry === page ? 'page' : undefined}
              aria-label={tCommon.pageNumber.replace('{n}', String(entry + 1))}
              className={cn(
                'inline-flex h-8 min-w-8 items-center justify-center rounded-md border px-2 text-sm font-medium tabular-nums transition-colors',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
                entry === page
                  ? 'border-indigo-600 bg-indigo-600 text-white'
                  : 'border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800',
              )}>
              {entry + 1}
            </button>
          ),
        )}

        <button
          type="button"
          onClick={() => goTo(page + 1)}
          disabled={page >= totalPages - 1}
          aria-label={tCommon.nextPage}
          className={arrowClasses}>
          <ChevronRight size={16} aria-hidden />
        </button>
      </nav>
    </div>
  );
};

export default TablePagination;
